/**
 * Video capability detection and quality profiles for WebRTC video calls
 */

export type VideoQuality = 'auto' | 'sd' | 'hd' | 'fhd';

export interface VideoResolution {
  width: number;
  height: number;
  label: string;
}

export interface VideoCapability {
  hasCamera: boolean;
  hasVideoCodec: boolean;
  supportedResolutions: VideoResolution[];
  recommendedQuality: VideoQuality;
  availableCameras: MediaDeviceInfo[];
}

export interface VideoQualityProfile {
  name: VideoQuality;
  width: number;
  height: number;
  maxBitrate: number;
  maxFramerate: number;
  aspectRatio: number;
}

export interface RtpStreamStats {
  resolution: string;
  frameWidth: number;
  frameHeight: number;
  fps: number;
  bitrate: number;
  bytesTotal: number;
  packetsTotal: number;
  packetsLost: number;
  jitter?: number;
  codec?: string;
}

export interface VideoStats {
  timestamp: number;
  inbound?: RtpStreamStats;
  outbound?: RtpStreamStats;
  inboundAudioCodec?: string;
  outboundAudioCodec?: string;
}

export interface VideoConstraints extends MediaTrackConstraints {
  width?: ConstrainULong;
  height?: ConstrainULong;
  frameRate?: ConstrainULong;
  aspectRatio?: ConstrainDouble;
  deviceId?: ConstrainDOMString;
}

/**
 * Maximum outgoing video bitrate (3 Mbit/s).
 * This is optimal for Full HD (1080p24) video quality.
 * The browser's WebRTC encoder automatically scales resolution within this limit.
 */
export const MAX_VIDEO_BITRATE = 3000000;

/**
 * Video quality profiles for WebRTC video calls.
 * Auto and FHD modes use MAX_VIDEO_BITRATE (3 Mbit/s, optimal for 1080p24).
 * All modes operate in adaptive mode automatically.
 */
export const VIDEO_QUALITY_PROFILES: Record<VideoQuality, VideoQualityProfile> = {
  sd: {
    name: 'sd',
    width: 640,
    height: 480,
    maxBitrate: 500000, // 500 kbps
    maxFramerate: 24,
    aspectRatio: 4 / 3
  },
  hd: {
    name: 'hd',
    width: 1280,
    height: 720,
    maxBitrate: 1500000, // 1.5 Mbps
    maxFramerate: 30,
    aspectRatio: 16 / 9
  },
  fhd: {
    name: 'fhd',
    width: 1920,
    height: 1080,
    maxBitrate: MAX_VIDEO_BITRATE,
    maxFramerate: 30,
    aspectRatio: 16 / 9
  },
  auto: {
    name: 'auto',
    width: 1920,
    height: 1080,
    maxBitrate: MAX_VIDEO_BITRATE,
    maxFramerate: 24,
    aspectRatio: 16 / 9
  }
};

export const SUPPORTED_VIDEO_CODECS = [
  'video/VP8',
  'video/VP9',
  'video/H264',
  'video/H265'
];

export const STANDARD_RESOLUTIONS: VideoResolution[] = [
  { width: 320, height: 240, label: 'QVGA (320x240)' },
  { width: 640, height: 480, label: 'VGA (640x480)' },
  { width: 1280, height: 720, label: 'HD (1280x720)' },
  { width: 1920, height: 1080, label: 'Full HD (1920x1080)' }
];
